import { useRef, useState, useEffect, useCallback } from "react";
import { Music, VolumeX } from "lucide-react";

// "Mister Softee" jingle — THE classic New York ice-cream-truck tune (key of C)
const FREQ = {
  "1": 261.63, "2": 293.66, "3": 329.63, "4": 349.23, "5": 392.0, "6": 440.0, "7": 493.88,
  "1°": 523.25, "2°": 587.33, "3°": 659.25, "4°": 698.46, "5°": 783.99, "6°": 880.0, "7°": 987.77,
};
const TAB_LINES = [
  "3° 5° 6° 5° 3° 2° 1° 2° 1° 6 1° 7 1° 2° 5° 3° 1°",
  "5° 6° 5° 3° 2° 1° 2° 1° 6 1° 7 1° 2° 5° 3° 1°",
  "1° 2° 5 5 3° 2° 1° 1° 2° 5 5 3°",
  "1° 2° 5 5 3° 4° 5° 2° 3° 4° 2° 5°",
  "5° 5° 6° 5° 3° 2° 1° 2° 1° 6 1° 7 1° 2° 5° 3° 1°",
  "5° 6° 5° 3° 2° 1° 2° 1° 6 1° 7 1° 2° 5° 3° 1°",
  "2° 3° 4° 2° 3° 4° 5° 6° 7 2° 1°",
];
// Slower tempo + lilting swing, with held cadence notes and a breath between phrases.
const BEAT = 0.3;
const MELODY = [];
TAB_LINES.forEach((line) => {
  const toks = line.split(/\s+/);
  toks.forEach((tok, i) => {
    const isLast = i === toks.length - 1;
    let dur;
    if (isLast) dur = BEAT * 2.2;          // held note at end of phrase
    else dur = i % 2 === 0 ? BEAT * 1.18 : BEAT * 0.82; // long-short lilt
    MELODY.push({ freq: FREQ[tok] || 0, dur });
  });
  MELODY.push({ freq: 0, dur: BEAT * 0.7 }); // breath between phrases
});

export default function MusicPlayer() {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const timerRef = useRef(null);
  const idxRef = useRef(0);
  const nextTimeRef = useRef(0);
  const [playing, setPlaying] = useState(false);

  const playNote = (ctx, master, freq, time, dur) => {
    const osc = ctx.createOscillator();
    const harm = ctx.createOscillator();
    const g = ctx.createGain();
    const hg = ctx.createGain();
    osc.type = "triangle";
    harm.type = "sine";
    osc.frequency.value = freq;
    harm.frequency.value = freq * 2;
    hg.gain.value = 0.25;
    osc.connect(g);
    harm.connect(hg).connect(g);
    g.connect(master);
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(1, time + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0008, time + dur * 0.95);
    osc.start(time);
    harm.start(time);
    osc.stop(time + dur);
    harm.stop(time + dur);
  };

  const scheduler = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    while (nextTimeRef.current < ctx.currentTime + 0.25) {
      const n = MELODY[idxRef.current % MELODY.length];
      if (n.freq) playNote(ctx, masterRef.current, n.freq, nextTimeRef.current, n.dur);
      nextTimeRef.current += n.dur;
      idxRef.current += 1;
    }
  }, []);

  const start = useCallback(() => {
    setPlaying(true);
    try {
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        const master = ctx.createGain();
        master.gain.value = 0.09;
        master.connect(ctx.destination);
        ctxRef.current = ctx;
        masterRef.current = master;
      }
      const ctx = ctxRef.current;
      const resumed = ctx.resume();
      if (resumed && resumed.catch) resumed.catch(() => {});
      nextTimeRef.current = ctx.currentTime + 0.1;
      if (!timerRef.current) timerRef.current = setInterval(scheduler, 25);
    } catch (e) {
      /* audio unavailable (e.g. autoplay restrictions) */
    }
  }, [scheduler]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (ctxRef.current) ctxRef.current.suspend();
    setPlaying(false);
  }, []);

  const toggle = () => {
    if (playing) {
      localStorage.setItem("sc_music", "off");
      stop();
    } else {
      localStorage.setItem("sc_music", "on");
      start();
    }
  };

  // Autostart on first user interaction (unless previously muted)
  useEffect(() => {
    if (localStorage.getItem("sc_music") === "off") return;
    let done = false;
    const onFirst = () => {
      if (done) return;
      done = true;
      start();
      cleanup();
    };
    const evs = ["pointerdown", "keydown", "touchstart"];
    const cleanup = () => evs.forEach((e) => window.removeEventListener(e, onFirst));
    evs.forEach((e) => window.addEventListener(e, onFirst, { passive: true }));
    return cleanup;
  }, [start]);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  return (
    <button
      onClick={toggle}
      data-testid="music-toggle"
      aria-label={playing ? "Mute music" : "Play music"}
      title={playing ? "Mute the gelato tune" : "Play the gelato tune"}
      className="fixed bottom-5 left-5 z-50 flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-sc-espresso text-sc-cream shadow-[0_8px_24px_rgba(44,42,40,0.3)] hover:scale-105 active:scale-95 transition-transform"
    >
      <span className="relative grid place-items-center w-6 h-6">
        {playing ? <Music size={18} /> : <VolumeX size={18} />}
        {playing && (
          <span className="absolute -right-0.5 -top-0.5 w-2 h-2 rounded-full bg-sc-sage animate-ping" />
        )}
      </span>
      <span className="text-xs font-medium hidden sm:inline">
        {playing ? "Gelato tune" : "Play tune"}
      </span>
    </button>
  );
}
